import { fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { not } from 'ember-truth-helpers';
import moment from 'moment-timezone';
import TasksModal from 'volta/components/tasks/modal';
import TasksTriggerBtn from 'volta/components/tasks/trigger-btn';
import can from 'volta/helpers/can';
import orElse from 'volta/helpers/or-else';
import useState from 'volta/helpers/use-state';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { dateCriticality } from 'volta/utils/style-utils';

import type { ComponentLike } from '@glint/template';
import type { IResourceName } from 'volta/models/base-model';
import type { TDate } from 'volta/models/types/calendar';
import type { IAggTasksViewMap, TTaskType, UTask } from 'volta/models/types/tasks';
import type StoreService from 'volta/services/store';
import type TaskService from 'volta/services/task-service';
import type { TError } from 'volta/utils/error-utils';
import type { TasksTriggerBtnSignature } from './trigger-btn';

export interface TasksSignature {
  Args: {
    taskType: TTaskType;
    entityId?: string | null;
    entityResourceName: IResourceName;
    aggStats?: IAggTasksViewMap;
    disabled?: boolean;
    disabledEdition?: boolean;
    minimized?: boolean;
    inPage?: boolean;
    groupingField?: 'dueDate';
    isAutoResolvable?: boolean;
    scrollToDate?: TDate;
    showDueDate?: boolean;
    authorizedUsers?: string[];
    translucentBackdrop?: boolean;
    onTaskUpdated?: (task: UTask[]) => void;
    onClose?: () => void;
  };
  Blocks: {
    default: [
      {
        visible: boolean;
        show: () => void;
        hide: () => void;
        toggle: () => void;
        onTrigger: () => void;
        TriggerBtn: ComponentLike<TasksTriggerBtnSignature>;
      }
    ];
  };
  Element: HTMLElement;
}

export default class TasksComponent extends Component<TasksSignature> {
  @service store!: StoreService;
  @service taskService!: TaskService;

  @tracked tasks: UTask[] = [];

  get allowedDuedTaskTypes(): string[] {
    return [this.args.taskType];
  }

  get isLoading(): boolean {
    return this.fetchTasks.isRunning;
  }

  get tasksCount(): number {
    const { aggStats } = this.args;

    if (!aggStats && this.tasks.filter((task) => !task.resolvedAt).length === 0) {
      return 0;
    }

    return Object.entries(aggStats ?? {}).reduce((acc, [taskType, aggStats]) => {
      if (!aggStats) {
        return acc;
      }
      if (this.allowedDuedTaskTypes.includes(taskType)) {
        acc += aggStats.total;
      }
      return acc;
    }, 0);
  }

  get aggStatsMinDueDate(): TDate | undefined {
    const { aggStats } = this.args;
    if (!aggStats) {
      return undefined;
    }

    return Object.entries(aggStats).reduce((acc: string | undefined, [taskType, aggStats]) => {
      if (!aggStats) {
        return acc;
      }
      if (this.allowedDuedTaskTypes.includes(taskType) && aggStats.minDueDate) {
        if (!acc) {
          acc = aggStats.minDueDate;
        } else if (moment.utc(aggStats.minDueDate).isBefore(acc)) {
          acc = aggStats.minDueDate;
        }
      }
      return acc;
    }, undefined);
  }

  get criticality() {
    const { aggStats } = this.args;

    if (!aggStats) {
      return undefined;
    }
    return dateCriticality(this.aggStatsMinDueDate);
  }

  fetchTasks = restartableTask(async () => {
    if (!this.args.entityId) {
      return [];
    }
    try {
      switch (this.args.taskType) {
        case 'PLANNING_TASK': {
          this.tasks = await this.taskService.fetchPlanningTasks.perform(this.args.entityId);
          return;
        }
        case 'EXECUTION_TASK': {
          this.tasks = await this.taskService.fetchExecutionTasks.perform(this.args.entityId);
          return;
        }
        case 'SKU_TASK': {
          this.tasks = await this.taskService.fetchSkuTasks.perform(this.args.entityId);
          return;
        }
        case 'FORECAST_TASK': {
          this.tasks = await this.taskService.fetchForecastsTasks.perform(this.args.entityId);
          return;
        }
        case 'SIMULATION_TASK': {
          this.tasks = await this.taskService.fetchSimulationTasks.perform(this.args.entityId);
          return;
        }
        case 'SIMULATION_ITEM_TASK': {
          this.tasks = await this.taskService.fetchSimulationItemSettingTasks.perform(
            this.args.entityId
          );
          return;
        }
        case 'FORECAST_PROJECT_TASK': {
          this.tasks = await this.taskService.fetchForecastProjectTasks.perform(this.args.entityId);
          return;
        }
        default: {
          return [];
        }
      }
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });

  @action
  onDraftTask(task?: UTask) {
    if (task) {
      this.tasks = [...this.tasks, task];
      this.args.onTaskUpdated?.(this.tasks);
    }
  }

  @action
  onSaveTask(task?: UTask) {
    this.tasks = this.tasks
      .filter((t) => t?.id && t.id !== task?.id)
      .concat(task?.id ? [task] : []);
    this.args.onTaskUpdated?.(this.tasks);
  }

  @action
  onDeleteTask(task?: UTask) {
    const withoutDeleted = this.tasks.filter((t) => t?.id && t.id !== task?.id);
    if (withoutDeleted.length !== this.tasks.length) {
      this.tasks = withoutDeleted;
      this.args.onTaskUpdated?.(this.tasks);
    }
  }

  onCloseTasks = dropTask(async (task?: UTask) => {
    if (!task) {
      return;
    }
    if (task.newWithoutComment) {
      this.onDeleteTask(task);
    } else {
      await this.taskService.saveTask.perform(task);
    }
  });

  <template>
    {{#if (can 'ViewTasks' @entityResourceName.plural)}}
      {{#let (useState false) as |tasksVisible|}}
        {{#let
          (queue (perform this.fetchTasks) (fn tasksVisible.setState (not tasksVisible.state)))
          as |triggerFunction|
        }}
          {{#let
            (component
              TasksTriggerBtn
              onClick=triggerFunction
              disabled=@disabled
              criticality=this.criticality
              minDueDate=this.aggStatsMinDueDate
              showDueDate=@showDueDate
              isLoading=this.isLoading
              taskType=@taskType
              minimized=@minimized
              tasksCount=this.tasksCount
              pressed=tasksVisible.state
            )
            as |TriggerBtn|
          }}
            {{#if (has-block)}}
              {{yield
                (hash
                  visible=tasksVisible.state
                  show=(fn tasksVisible.setState true)
                  hide=(fn tasksVisible.setState false)
                  toggle=(fn tasksVisible.setState (not tasksVisible.state))
                  onTrigger=triggerFunction
                  TriggerBtn=TriggerBtn
                )
              }}
            {{else}}
              <TriggerBtn ...attributes />
            {{/if}}
            {{#if tasksVisible.state}}
              <TasksModal
                @inPage={{@inPage}}
                @entityId={{@entityId}}
                @tasks={{this.tasks}}
                @taskType={{@taskType}}
                @groupingField={{orElse @groupingField 'dueDate'}}
                @onClose={{fn tasksVisible.setState false}}
                @onDestroy={{perform this.onCloseTasks}}
                @onDeleteTask={{this.onDeleteTask}}
                @onDraftTask={{this.onDraftTask}}
                @onSaveTask={{this.onSaveTask}}
                @isAutoResolvable={{orElse @isAutoResolvable true}}
                @scrollToDate={{@scrollToDate}}
                @isLoading={{this.isLoading}}
                @entityResourceName={{@entityResourceName}}
                @disabled={{@disabledEdition}}
                @authorizedUsers={{@authorizedUsers}}
                @translucentBackdrop={{@translucentBackdrop}}
              />
            {{/if}}
          {{/let}}
        {{/let}}
      {{/let}}
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    Tasks: typeof TasksComponent;
    tasks: typeof TasksComponent;
  }
}
